import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Megaphone, Plus, Users, Image as ImageIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/admin/broadcasts/")({
  component: BroadcastsListPage,
});

type Broadcast = {
  id: string;
  title: string;
  body: string;
  url: string;
  image_url: string | null;
  audience: "all" | "paid" | "free";
  recipients_count: number;
  created_at: string;
};

function BroadcastsListPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["push-broadcasts"],
    queryFn: async (): Promise<Broadcast[]> => {
      const { data, error } = await supabase
        .from("push_broadcasts")
        .select("id, title, body, url, image_url, audience, recipients_count, created_at")
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw new Error(error.message);
      return (data ?? []) as Broadcast[];
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-gold" />
            <h1 className="font-display text-3xl font-bold">Broadcasts</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Send custom push notifications to subscribed users. Use sparingly to protect signal-channel trust.
          </p>
        </div>
        <Button asChild>
          <Link to="/admin/broadcasts/new">
            <Plus className="h-4 w-4" /> New broadcast
          </Link>
        </Button>
      </div>

      <div className="rounded-xl border border-border/60 bg-card">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading.</div>
        ) : !data || data.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">No broadcasts sent yet.</div>
        ) : (
          <ul className="divide-y divide-border/60">
            {data.map((b) => (
              <li key={b.id} className="flex items-start gap-4 p-4">
                {b.image_url ? (
                  <img
                    src={b.image_url}
                    alt="Broadcast image preview"
                    className="h-16 w-28 flex-shrink-0 rounded-md border border-border/60 object-cover"
                  />
                ) : (
                  <div className="flex h-16 w-28 flex-shrink-0 items-center justify-center rounded-md border border-dashed border-border/60 text-muted-foreground">
                    <ImageIcon className="h-5 w-5" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-sm font-bold">{b.title}</span>
                    <span className="rounded-full border border-border/60 px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                      {b.audience}
                    </span>
                  </div>
                  <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{b.body}</p>
                  <p className="mt-1 truncate text-xs text-muted-foreground/80">{b.url}</p>
                </div>
                <div className="flex flex-col items-end gap-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {b.recipients_count}
                  </span>
                  <span>{new Date(b.created_at).toLocaleString()}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
